/**
 * Runtime for the generated Velora API client (BUILD-PLAN 2.3).
 * WP0-API generates typed endpoint functions into src/generated/ from the
 * OpenAPI document; they all go through `createHttpClient`. Do not hand-edit
 * src/generated/.
 */
import { ErrorEnvelopeSchema } from '@velora/contracts';
import type { z } from 'zod';

export interface HttpClientOptions {
  /** Origin plus prefix, for example `/v1` in the browser or `http://localhost:3001/v1` in tests. */
  baseUrl: string;
  fetch?: typeof fetch;
  /** Extra headers on every request (CSRF token, office scope). */
  headers?: () => Record<string, string>;
}

/** Thrown for any non-2xx response. `body` is the parsed error envelope when JSON. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    readonly path: string,
  ) {
    super(`API ${status} on ${path}`);
    this.name = 'ApiError';
  }

  private envelope() {
    const parsed = ErrorEnvelopeSchema.safeParse(this.body);
    return parsed.success ? parsed.data.error : null;
  }

  /** The envelope's machine-readable code (for example `entitlement_required`), if any. */
  get code(): string | null {
    return this.envelope()?.code ?? null;
  }

  /** The envelope's `details` (validation issues, upsell data...), if any. */
  get details(): unknown {
    return this.envelope()?.details;
  }

  /** Request id to quote to support, from the envelope. */
  get requestId(): string | null {
    return this.envelope()?.requestId ?? null;
  }
}

type QueryValue =
  string | number | boolean | null | undefined | ReadonlyArray<string | number | boolean>;

/**
 * Fills `:name` segments from `params` (URI-encoded) and appends the defined
 * query values. Used by the generated client.
 */
export function buildPath(
  template: string,
  params?: Readonly<Record<string, string | number>>,
  query?: Readonly<Record<string, QueryValue>>,
): string {
  const path = template.replace(/:([A-Za-z0-9_]+)/g, (_m, name: string) => {
    const v = params?.[name];
    if (v === undefined) throw new Error(`missing path parameter ${name}`);
    return encodeURIComponent(String(v));
  });
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v))
      for (const item of v as ReadonlyArray<unknown>) qs.append(k, String(item));
    else qs.append(k, String(v));
  }
  const q = qs.toString();
  return q ? `${path}?${q}` : path;
}

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Response schema; the response is validated so contract drift fails loudly. */
  schema: z.ZodType<T>;
  signal?: AbortSignal;
}

export interface HttpClient {
  request<T>(path: string, options: RequestOptions<T>): Promise<T>;
}

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const base = options.baseUrl.replace(/\/+$/, '');
  return {
    async request<T>(path: string, req: RequestOptions<T>): Promise<T> {
      const url = `${base}/${path.replace(/^\/+/, '')}`;
      const headers: Record<string, string> = {
        accept: 'application/json',
        ...options.headers?.(),
      };
      const init: RequestInit = { method: req.method ?? 'GET', headers, credentials: 'include' };
      if (req.signal) init.signal = req.signal;
      if (req.body !== undefined) {
        headers['content-type'] = 'application/json';
        init.body = JSON.stringify(req.body);
      }
      const res = await doFetch(url, init);
      const text = await res.text();
      const parsed: unknown = text.length > 0 ? safeJson(text) : undefined;
      if (!res.ok) throw new ApiError(res.status, parsed, path);
      return req.schema.parse(parsed);
    },
  };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
